/**
 * Pure helpers for the JWT access tokens issued by `POST /api/v2/token` and `POST /api/v2/token/refresh`.
 *
 * The payload is decoded without verifying the signature: the API verifies it on every request, the client
 * only needs the claims to know who is logged in and when the token expires.
 */

export type AccessTokenClaims = {
    uid: number;
    username: string;
    roles: string[];
    /** expiry, in seconds since the epoch */
    exp: number;
    /** scheme + host of the API that issued the token; a token is only accepted by that host */
    iss: string;
};

const decodeBase64Url = (value: string): string => {
    const base64 = value
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .padEnd(Math.ceil(value.length / 4) * 4, '=');
    const bytes = Uint8Array.from(atob(base64), char => char.charCodeAt(0));

    return new TextDecoder().decode(bytes);
};

export const decodeAccessToken = (jwt: string | null | undefined): AccessTokenClaims | null => {
    const payload = jwt?.split('.')[1];

    if (!payload) {
        return null;
    }

    try {
        const claims = JSON.parse(decodeBase64Url(payload));

        if ('number' !== typeof claims?.exp || 'number' !== typeof claims.uid) {
            return null;
        }

        return {
            uid: claims.uid,
            username: String(claims.username ?? ''),
            roles: String(claims.roles ?? '')
                .split(' ')
                .filter(role => '' !== role),
            exp: claims.exp,
            iss: String(claims.iss ?? ''),
        };
    } catch {
        return null;
    }
};

/** Whether the token is expired, or will be within `seconds` from `now` (milliseconds). */
export const expiresWithin = (claims: AccessTokenClaims | null, seconds: number, now: number = Date.now()): boolean =>
    null === claims || claims.exp * 1000 <= now + seconds * 1000;
